import Fuse from "fuse.js";
import produce from "immer";
import type { GetState, SetState } from "zustand";

import type { FilterOptions, GridColumn, IAvailableSummarizeField } from "../../../../schemas/report";

import { getRbacPrimaryRolePermissions } from "../../../../../../api/services/defs/rbac";

import { selectQuestionTypeDisplayName } from "../../utils";

import type { IQuestion } from "../../Components/QuestionGroupingModal/schema";
import Config from "../../Config";
import type { QuestionGroupingModalState, ReportStore } from "../model";
import {
	fetchGroupingSession,
	fetchNonUniqueQuestions,
	fetchUniqueQuestions,
	fetchUniqueReviewCycles,
	fetchUniqueReviewForms,
	removeQuestionGrouping,
} from "../requests";

const reviewReportStore = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	/**
	 * FEATURE: Unique Questions
	 */
	groupedQuestions: [],
	groupingSession: null,
	employeeFields: [],
	hasGroupableQuestions: true,
	uniqueReviewReportQuestions: [],
	uniqueReviewReportQuestionsMap: {},
	uniqueReviewReportQuestionsFuse: null,
	nonUniqueReviewReportQuestions: [],
	nonUniqueReviewReportQuestionsMap: {},

	fetchAvailableReviewColumns: async () => {
		const { columnMetadata, fetchQuestions, fetchGroupedQuestions, fetchAvailableEmployeeColumns } = get();

		// employee field columns
		const [allEmployeeFieldsAsColumns] = await Promise.all([
			fetchAvailableEmployeeColumns(),
			fetchQuestions(),
			fetchGroupedQuestions(),
		]);

		// filter out some employee fields that aren't needed for review reports
		const filteredEmployeeFieldsAsColumns = allEmployeeFieldsAsColumns.filter(
			(field) => field.field !== "employee_Employee"
		);

		// need to get() grouped questions after fetchGroupedQuestions runs
		const groupedQuestions = get().groupedQuestions;
		const user_permissions = await getRbacPrimaryRolePermissions("report_analytics");

		// return sorted questions
		const uniqueQuestionsAsColumns: GridColumn[] = get()
			.uniqueReviewReportQuestions.map((question) => ({
				field: `question_${question._id}`,
				headerName: question.question,
				sortable: true,
				pinned: null,
				fieldType: "UNIQUE_QUESTION" as const,
				fieldValueType: question.questionType,
				fieldPath: ["questions", question._id, "answer"],
				columnMetadata: { ...columnMetadata[`question_${question._id}`], min: question.min, max: question.max },
			}))
			.sort((a, b) =>
				a.headerName.localeCompare(b.headerName, undefined, { numeric: true, sensitivity: "base" })
			);

		const nonUniqueQuestionsAsColumns: GridColumn[] = get().nonUniqueReviewReportQuestions.map((question) => ({
			field: `question_${question._id}`,
			headerName: question.question,
			sortable: true,
			pinned: null,
			fieldType: "NON_UNIQUE_QUESTION",
			fieldValueType: question.questionType,
			fieldPath: ["questions", question._id, "answer"],
			columnMetadata: { ...columnMetadata[`question_${question._id}`], min: question.min, max: question.max },
		}));

		let groupedNonUniqueQuestionsAsColumns: GridColumn[] = [];

		if (groupedQuestions.length) {
			groupedNonUniqueQuestionsAsColumns = groupedQuestions
				.map((question) => {
					const firstNonUniqueQuestion = nonUniqueQuestionsAsColumns.find(
						(nonUniqueQuestion) => nonUniqueQuestion.field === `question_${question.questionsInGroup[0]}`
					);

					const fieldValueType = firstNonUniqueQuestion?.fieldValueType;

					const gridColumn: GridColumn = {
						field: `groupedquestion_${question.id}`,
						headerName: question.label,
						sortable: true,
						pinned: null,
						fieldType: "GROUPED_QUESTION",
						fieldValueType: fieldValueType,
						fieldPath: ["groupedQuestions", question.id, "answer"],
						columnMetadata: columnMetadata[`groupedquestion_${question._id}`],
					};

					return gridColumn;
				})
				.filter((column) => column.fieldValueType != null);
		}

		const reviewFormFieldsAsColumns: GridColumn[] = Object.keys(Config.reviewFormFields).map((fieldID) => {
			const field = Config.reviewFormFields[fieldID];

			const column: GridColumn = {
				field: `reviewform_${field._id}`,
				headerName: field.headerName,
				sortable: true,
				pinned: null,
				fieldType: "REVIEW_FORM_FIELD",
				fieldValueType: field.type,
				fieldPath: ["review_form", field._id],
				columnMetadata: columnMetadata[`reviewform_${field._id}`],
			};

			// Only include valueGetter if field.valueGetter is defined
			if (field.valueGetter) {
				column.valueGetter = field.valueGetter;
			}

			return column;
		});

		const reviewFieldsAsColumnsRaw: GridColumn[] = Object.keys(Config.reviewFields).map((fieldID) => {
			const field = Config.reviewFields[fieldID];

			return {
				field: `review_${field._id}`,
				headerName: field.headerName,
				sortable: true,
				pinned: null,
				fieldType: "REVIEW_FIELD",
				fieldValueType: field.type,
				fieldPath: ["review", field._id],
				columnMetadata: columnMetadata[`review_${field._id}`],
			};
		});

		let reviewFieldsAsColumns: GridColumn[] = reviewFieldsAsColumnsRaw;
		if (!user_permissions.includes("report_analytics:createReport")) {
			reviewFieldsAsColumns = reviewFieldsAsColumnsRaw.filter((field) => field.field !== "review_participants");
		}

		const reviewCycleFieldsAsColumns: GridColumn[] = Object.keys(Config.reviewCycleFields).map((fieldID) => {
			const field = Config.reviewCycleFields[fieldID];

			return {
				field: `reviewcycle_${field._id}`,
				headerName: field.headerName,
				sortable: true,
				pinned: null,
				fieldType: "REVIEW_CYCLE_FIELD",
				fieldValueType: field.type,
				fieldPath: ["review_cycle", field._id],
				columnMetadata: columnMetadata[`reviewcycle_${field._id}`],
			};
		});

		const availableGridColumns = [
			...filteredEmployeeFieldsAsColumns,
			...uniqueQuestionsAsColumns,
			...reviewFormFieldsAsColumns,
			...reviewFieldsAsColumns,
			...reviewCycleFieldsAsColumns,
			...groupedNonUniqueQuestionsAsColumns,
		];

		return availableGridColumns;
	},

	fetchAvailableReviewSummarizeFields: async (): Promise<IAvailableSummarizeField[]> => {
		const employeeFields = get().getAvailableEmployeeSummarizeFields();

		await get().fetchGroupedQuestions();
		const groupedQuestions = get().groupedQuestions;

		const staticFields = Config.summarizeFields;

		// add grouped questions to available summarize fields
		const groupedQuestionFields = groupedQuestions
			.map((group) => {
				const columnInfo = get().availableGridColumns.find(
					(availableColumn) => availableColumn.field === `groupedquestion_${group.id}`
				);
				const isNumeric = Config.numericTypes.includes(columnInfo?.fieldValueType as string);

				// filter out non-numeric fields
				if (isNumeric) {
					return {
						value: `groupedquestion_${group.id}`,
						label: group.label,
						group: "QUESTION_GROUPS",
						type: "groupedquestion",
					};
				}

				return null;
			})
			.filter((field) => field !== null);

		const allSummarizeFields = [
			...employeeFields,
			...staticFields.reviewcycle,
			...staticFields.review,
			...staticFields.reviewform,
			...groupedQuestionFields,
		] as IAvailableSummarizeField[];

		return allSummarizeFields;
	},

	fetchGroupedQuestions: async () => {
		const { client, report } = get();
		if (!client || !report) return;

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");
		const questionGroupingSession = await fetchGroupingSession(client, report.questionGroupingSessionID ?? "");

		const groupingSession = {
			reviewQueryID: questionGroupingSession?.reviewQueryID ?? null,
		};

		const groupedQuestions =
			questionGroupingSession?.selectedQuestions
				.filter((question) => {
					return question.isGrouped;
				})
				.map((question) => {
					return {
						id: question._id,
						label: question.name,
						questionsInGroup: question.questions,
						summaryFunction: question.summaryFunction,
					};
				}) ?? [];
		set({ groupedQuestions, groupingSession });
	},

	/**
	 * Ungroups a question group for this report. If the question group is selected as
	 * a column, selects all of the unselected individual questions from the question group
	 * as columns.
	 */
	ungroupGroupedQuestion: async (groupID) => {
		// gets the question group in question
		const questionGroup = get().groupedQuestions.find((group) => group.id === groupID);
		// if not found do nothing
		if (!questionGroup) {
			console.error("No question group found");
			return;
		}
		// beacause enabledGridColumns uses {column-type}_{column-id} format
		// (e.g. groupedquestion_62825b4f3e187af001df0752)
		const columnIsGroup = (column: string) => column.split("_")[1] === questionGroup.id;

		// if selected, do column selection logic:
		if (get().enabledGridColumns.find(columnIsGroup)) {
			// get all selected columns that are not the group
			const alreadyEnabledColumns = get().enabledGridColumns.filter((col) => !columnIsGroup(col));

			// update the enabledGridColumns array with columns from questionGroup not already selected
			set({
				enabledGridColumns: [...alreadyEnabledColumns],
			});
		}

		const { client, report } = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		// actually delete the question group
		await removeQuestionGrouping(client, report.questionGroupingSessionID ?? "", groupID);

		// fetch the new grouped questions
		await get().fetchGridData(false);
		await get().fetchAvailableColumns();
	},

	fetchQuestions: async () => {
		try {
			const { client, report } = get();

			if (!client) throw new Error("Client not initialized");
			if (!report) throw new Error("Report not initialized");

			const filters = get().getAppliedFilters();

			const questionGroupingSessionID = report.questionGroupingSessionID;
			const uniqueReviewReportQuestions =
				(await fetchUniqueQuestions(client, questionGroupingSessionID ?? "", filters)) ?? [];

			// fetch questions relevant to each report, not specific to a review report
			const nonUniqueReviewReportQuestions =
				(await fetchNonUniqueQuestions(client, questionGroupingSessionID ?? "")) ?? [];

			// index questions by _id
			const uniqueReviewReportQuestionsMap = uniqueReviewReportQuestions?.reduce((acc, question) => {
				acc[question._id] = question;
				return acc;
			}, {});

			const nonUniqueReviewReportQuestionsMap = nonUniqueReviewReportQuestions?.reduce((acc, question) => {
				acc[question._id] = question;
				return acc;
			}, {});

			// create a fuzzy search instance
			const uniqueReviewReportQuestionsFuse = new Fuse(uniqueReviewReportQuestions, {
				shouldSort: true,
				includeScore: false,
				threshold: 0.4,
				location: 0,
				distance: 1000,
				maxPatternLength: 32,
				minMatchCharLength: 1,
				keys: ["question", "questionType", "reviewFormName"],
			});

			const hasGroupableQuestions =
				uniqueReviewReportQuestions.filter((question) =>
					[...Config.numericTypes, ...Config.textTypes, ...Config.selectTypes].includes(question.questionType)
				).length > 0;

			set({
				hasGroupableQuestions,
				uniqueReviewReportQuestions,
				uniqueReviewReportQuestionsMap,
				uniqueReviewReportQuestionsFuse,
				nonUniqueReviewReportQuestions,
				nonUniqueReviewReportQuestionsMap,
			});
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * FEATURE: Question Grouping Modal
	 */
	questionGroupingModal: {
		name: "",
		selectedQuestions: [],
		modalHasCustomName: false,
		showOnlySelectedQuestions: false,
		modalError: null,
		state: "NEW" as QuestionGroupingModalState,
		id: null,

		/**
		 * Loads a question group into the modal (for editing a question group)
		 * @param {string} questionID - The id of the question group to edit. (question_grouping_session.selected_questions._id)
		 */
		loadQuestionGroup: (questionID) => {
			const questionToLoad = get().groupedQuestions.find((question) => question.id === questionID);

			// If the question is found, switch to edit mode and load the question group data
			if (questionToLoad !== undefined) {
				set(
					produce((state: ReportStore) => {
						state.questionGroupingModal.state = "EDIT" as QuestionGroupingModalState;
						state.questionGroupingModal.name = questionToLoad.label;
						state.questionGroupingModal.selectedQuestions = questionToLoad.questionsInGroup;
						state.questionGroupingModal.id = questionID;
					})
				);
			}
		},

		clearLoadedQuestionGroup: () => {
			set(
				produce((state: any) => {
					state.questionGroupingModal.name = "";
					state.questionGroupingModal.selectedQuestions = [];
					state.questionGroupingModal.modalHasCustomName = false;
					state.questionGroupingModal.showOnlySelectedQuestions = false;
					state.questionGroupingModal.modalError = null;
					state.questionGroupingModal.state = "NEW" as QuestionGroupingModalState;
					state.questionGroupingModal.id = null;
				})
			);
		},

		// unselects all selected questions. Called by the modal on close, create and edit
		clearSelectedQuestions: () => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.selectedQuestions = [];
				})
			);
		},

		/**
		 * Sets the Name to be used for the question group
		 *
		 * @param {string} name - The name to set in the modal.
		 */
		setName: (name) => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.name = name;
				})
			);
		},

		clearName: () => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.name = "";
				})
			);
		},

		clearShowSelectedOnly: () => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.showOnlySelectedQuestions = false;
				})
			);
		},

		toggleSelectedQuestion: (questionID) => {
			const { selectedQuestions } = get().questionGroupingModal;
			const index = selectedQuestions.indexOf(questionID);
			const action = index === -1 ? "add" : "remove";
			set(
				produce((state: ReportStore) => {
					if (action === "add") {
						state.questionGroupingModal.selectedQuestions.push(questionID);
					} else {
						state.questionGroupingModal.selectedQuestions.splice(index, 1);
					}
				})
			);
		},
		// Sets properties in the question grouping modal
		setQuestionGroupModalValue: (props: any = {}) => {
			const { name, showOnlySelectedQuestions } = props;

			set(
				produce((state: ReportStore) => {
					// Name can intentionally be null
					if (typeof name !== "undefined") {
						state.questionGroupingModal.name = name;
						state.questionGroupingModal.modalError = null;
					}

					if (showOnlySelectedQuestions !== undefined) {
						state.questionGroupingModal.showOnlySelectedQuestions = showOnlySelectedQuestions;
					}
				})
			);
		},
		// Sets the error message for the Question Group Modal
		setQuestionGroupModalError: (error: string) => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.modalError = error;
				})
			);
		},
		// Returns the type and review form id of the currently selected
		// question if one exists.
		getSelectedQuestionInformation: () => {
			const { uniqueReviewReportQuestionsMap, questionGroupingModal } = get();
			const { selectedQuestions } = questionGroupingModal;

			const isSelection = selectedQuestions.length > 0;

			if (!isSelection) {
				return null;
			}

			// Get the type of the first selected question. We will only show questions of the same type.
			const selectedQuestionType = uniqueReviewReportQuestionsMap[selectedQuestions[0]]?.questionType;
			const selectedQuestionReviewFormID = uniqueReviewReportQuestionsMap[selectedQuestions[0]]?.reviewFormID;

			return {
				type: selectedQuestionType,
				reviewFormID: selectedQuestionReviewFormID,
			};
		},

		/**
		 * Returns an array of questions that match the search term.
		 * Some rules:
		 *  - This will always return questions that have been selected
		 *  - If showOnlySelectedQuestions is true, it will only return questions that have been selected
		 *  - If a selection is made, all questions will be of the same type
		 */
		getUniqueQuestions: (search) => {
			const {
				uniqueReviewReportQuestions,
				uniqueReviewReportQuestionsMap,
				questionGroupingModal,
				uniqueReviewReportQuestionsFuse,
			} = get();
			const { showOnlySelectedQuestions, selectedQuestions, getSelectedQuestionInformation } =
				questionGroupingModal;

			// Get the type of the first selected question. We will only show questions of the same type.
			const selectedQuestionInformation = getSelectedQuestionInformation();

			// get an array of the selected questions. These will always show on top.
			const selectedQuestionsArray: IQuestion[] = selectedQuestions
				.map((id) => {
					return uniqueReviewReportQuestionsMap[id];
				})
				.filter((value) => value !== null && value !== undefined);

			// get an array of all review form ids that are selected
			const selectedReviewFormIDs = selectedQuestionsArray?.map((question) => question?.reviewFormID);

			// fuzzy search the questions iff we have a search term
			let questions: IQuestion[] =
				search && search.length > 0
					? (uniqueReviewReportQuestionsFuse?.search(search).filter((question) => !!question) as IQuestion[])
					: uniqueReviewReportQuestions;

			// we are only allowing numeric text and select (multi/single) types at this time
			// if there are no selected questions, we will filter out all questions that are not numeric or text
			// if there are selected questions, we will filter out all questions that are not the same type as the selected questions
			if (selectedQuestionsArray.length === 0) {
				const allowedQuestionTypes = [...Config.numericTypes, ...Config.textTypes, ...Config.selectTypes];
				questions = questions.filter((question) => allowedQuestionTypes.includes(question.questionType));
			} else if (
				selectedQuestionsArray[0]?.questionType &&
				Config.numericTypes.includes(selectedQuestionsArray[0].questionType)
			) {
				questions = questions.filter((question) => Config.numericTypes.includes(question.questionType));
			} else if (
				selectedQuestionsArray[0]?.questionType &&
				Config.textTypes.includes(selectedQuestionsArray[0].questionType)
			) {
				questions = questions.filter((question) => Config.textTypes.includes(question.questionType));
			} else if (Config.selectTypes.includes(selectedQuestionsArray[0].questionType)) {
				questions = questions.filter((question) => Config.selectTypes.includes(question.questionType));
			}

			// filter out questions from any selected review forms
			if (selectedQuestionInformation) {
				questions = questions.filter((question) => !selectedReviewFormIDs.includes(question.reviewFormID));
			}

			if (showOnlySelectedQuestions) {
				questions = selectedQuestionsArray.filter((x) => !!x) as IQuestion[];
			} else {
				// add the selected questions to the top, remove selected questions from the searched list.
				questions = [
					...(selectedQuestionsArray.filter((x) => !!x) as IQuestion[]),
					...questions.filter((question) => !selectedQuestions.includes(question._id)),
				];
			}

			// the table expects the questions in a different format.
			// TODO - the table should just accept the questions directly
			const formattedQuestions = questions.map((item) => {
				return {
					id: item._id,
					question: item.question,
					// If its not a grouped question, then its a singleton with only
					// itself as part of the group.
					questionsInGroup: item.questionsInGroup || [item.questionHash],
					isGrouped: item.isGrouped || false,
					reviewForm: item.reviewFormName || "Unknown",
					questionType: selectQuestionTypeDisplayName(item.questionType),
				};
			});

			return formattedQuestions;
		},

		/**
		 * Clears any errors loaded into the modal
		 */
		clearModalError: () => {
			set(
				produce((state: ReportStore) => {
					state.questionGroupingModal.modalError = null;
				})
			);
		},

		/**
		 * Calls several functions to clear the modal, useful when closing the modal.
		 */
		clearModal: () => {
			get().questionGroupingModal.clearSelectedQuestions();
			get().questionGroupingModal.clearName();
			get().questionGroupingModal.clearShowSelectedOnly();
			get().questionGroupingModal.clearModalError();
			get().questionGroupingModal.clearLoadedQuestionGroup();
		},

		/**
		 * Delegate to the top-level getUniqueSurveyQuestions from surveyReportStore
		 */
		getUniqueSurveyQuestions: (search?: string) => {
			return get().getUniqueSurveyQuestions(search);
		},
	},

	fetchReviewFilterOptions: async (): Promise<FilterOptions> => {
		const { client, fetchEmployeeFilterOptions } = get();

		if (!client) throw new Error("Client not initialized");

		const employeeOptions = await fetchEmployeeFilterOptions();

		const [reviewCycles, reviewForms] = await Promise.all([
			fetchUniqueReviewCycles(client),
			fetchUniqueReviewForms(client),
		]);

		const reviewCycleNames = reviewCycles.map((reviewCycle) => ({
			value: reviewCycle._id,
			label: reviewCycle.name,
		}));

		const reviewFormNames = reviewForms.map((reviewForm) => ({
			value: reviewForm.name,
			label: reviewForm.name,
		}));

		return {
			...employeeOptions,
			reviewCycleNames,
			reviewFormNames,
		};
	},
});

export default reviewReportStore;
